import { useState, useEffect, useRef, useCallback } from 'react';
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface EngWritngProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
  registerSubmitValidator?: (validator: (() => Promise<boolean> | boolean) | null) => void;
}

export default function EngWritng({ activity, unitData, onResponse, existingResponse, isHomework = false, registerSubmitValidator }: EngWritngProps) {
    console.log('=== EngWritng 시작 ===');
    console.log('Activity:', activity);
    console.log('ConfigJson:', activity?.configJson);
    
    // ESSAY 타입: configJson에서 데이터 가져오기
    let activityConfig: any = {};
    try {
        if (activity?.configJson) {
            activityConfig = typeof activity.configJson === 'string'
                ? JSON.parse(activity.configJson)
                : activity.configJson;
        }
    } catch (e) {
        console.error('configJson 파싱 에러:', e);
    }
    
    // configJson에 데이터가 있으면 사용, 없으면 items에서
    const hasConfig = activityConfig.translation || activityConfig.fullText;
    let content: any = {};
    
    if (!hasConfig && activity?.items?.[0]) {
        try {
            const activityData = activity.items[0];
            content = activityData.contentJson ? JSON.parse(activityData.contentJson) : {};
        } catch (e) {
            console.error('contentJson 파싱 에러:', e);
        }
    }

    const sentence = activityConfig.translation || activityConfig.koreanSentence || content.korean || "우리는 또한 꽃들의 이름도 알지 못했다.";
    const correctAnswer = activityConfig.fullText || activityConfig.englishSentence || content.english || "We also didn't know the names of flowers.";
    const audioUrl = activityConfig.audioUrl || content.audio || null;
    const title = getStudentActivityTitle(activity?.title, '영작');
    
    console.log('Translation (해석):', sentence);
    console.log('Correct Answer (영어 문장):', correctAnswer);
    console.log('Audio URL:', audioUrl);

    const [inputText, setInputText] = useState(existingResponse?.inputText || '');
    const [wrongCount, setWrongCount] = useState(existingResponse?.wrongCount || 0);
    const [showAnswer, setShowAnswer] = useState(wrongCount >= 5);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const prevShowAnswerRef = useRef<boolean>(false);

    const getByteLength = (value: string) => {
        let len = 0;
        for (let i = 0; i < value.length; i++) {
            len += value.charCodeAt(i) > 127 ? 2 : 1;
        }
        return len;
    };

    const isHomeworkReady = (text: string) => {
        return getByteLength(text.trim()) >= 1;
    };

    const emitResponse = useCallback((nextText: string, extra: Record<string, any> = {}) => {
        if (!onResponse) return;
        onResponse({
            inputText: nextText,
            ...extra,
            isHomeworkReady: isHomeworkReady(nextText)
        });
    }, [onResponse]);

    // 마운트 시 초기 응답 저장
    useEffect(() => {
        if (onResponse && !existingResponse) {
            emitResponse('', {
                isCorrect: false,
                wrongCount: 0
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const prev = prevShowAnswerRef.current;
        if (!isHomework && showAnswer && !prev) {
            setInputText('');
            emitResponse('', {
                isCorrect: false,
                wrongCount
            });
        }
        prevShowAnswerRef.current = showAnswer;
    }, [showAnswer, isHomework, onResponse, wrongCount]);
    
    const handleWrongAnswer = useCallback((value?: string) => {
        if (isHomework) return;
        const nextWrong = wrongCount + 1;
        const reachedHintPhase = nextWrong >= 5 && !showAnswer;
        setWrongCount(nextWrong);
        if (nextWrong >= 5) {
            setShowAnswer(true);
        }

        if (reachedHintPhase) {
            setInputText('');
        }

        // 정답 노출 이후에는 입력 차단 없이 자유롭게 수정할 수 있어야 하므로 오디오 재생/포커스 이동을 생략한다.
        const audioElement = audioRef.current;
        const shouldReplayAudio = audioElement && audioUrl && !showAnswer;
        if (shouldReplayAudio && audioElement) {
            try {
                audioElement.currentTime = 0;
                audioElement.play().catch((error) => {
                    console.error('오디오 자동 재생 실패:', error);
                });
            } catch (error) {
                console.error('오디오 재생 중 오류:', error);
            }
        }

        const nextTextValue = reachedHintPhase ? '' : (value ?? inputText);
        emitResponse(nextTextValue, {
            isCorrect: false,
            wrongCount: nextWrong
        });
    }, [audioUrl, emitResponse, inputText, isHomework, showAnswer, wrongCount]);

    const handleAudioEnded = () => {
        if (inputRef.current) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    };

  const handleInputChange = (value: string) => {
    setInputText(value);
    const typedValue = value;
    const isExactMatch = typedValue === correctAnswer;

    if (isExactMatch) {
      setShowAnswer(false);
      emitResponse(value, {
        isCorrect: true,
        wrongCount
      });
      return;
    }

    emitResponse(value, {
      isCorrect: false,
      wrongCount
    });
  };

  useEffect(() => {
    if (!registerSubmitValidator) return;

    const validator = () => {
      const trimmed = inputText.trim();
      const hasMinLength = getByteLength(trimmed) >= 1;
      const isExactMatch = inputText === correctAnswer;

      if (isExactMatch) {
        emitResponse(inputText, {
          isCorrect: true,
          wrongCount
        });
        return true;
      }

      if (!hasMinLength) {
        emitResponse(inputText, {
          isCorrect: false,
          wrongCount
        });
        return false;
      }

      handleWrongAnswer(inputText);
      return false;
    };

    registerSubmitValidator(() => validator());
    return () => registerSubmitValidator(null);
  }, [registerSubmitValidator, inputText, correctAnswer, wrongCount, handleWrongAnswer]);

    const isCorrect = inputText === correctAnswer;
    const inputPlaceholder = showAnswer ? correctAnswer : '영어 문장을 입력해주세요';
    
  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
          <div className={styles.gameCard}>
            <div className='px-14 py-4'>
                <div className='relative'>
                    <p className='!text-2xl font-bold pb-2 text-center break-words'>{sentence}</p>
                </div>
            </div>
          </div>
            <section className='mt-5'>
                <p className='mb-3 text-sm text-center text-gray-500'>
                    띄어쓰기를 포함하여 완성된 문장을 입력해 주세요.
                    {!isHomework && wrongCount > 0 && wrongCount < 5 && (
                        <span className='text-red-500 ml-1'>(오답 {wrongCount}회)</span>
                    )}
                </p>
                <textarea
                    ref={inputRef}
                    value={inputText}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder={inputPlaceholder}
                    rows={4}
                    className={`${styles.graInput} resize-none${
                        showAnswer && !inputText ? ' bg-yellow-50 border-yellow-400 placeholder-yellow-700' : ''
                    }`}
                    style={{ paddingLeft: '1rem', paddingRight: '1rem' }}
                />
                {!isHomework && showAnswer && (
                    <p className='mt-2 text-sm text-orange-600 text-center font-semibold'>
                        정답: {correctAnswer}
                    </p>
                )}
            </section>
            {!isHomework && audioUrl && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={handleAudioEnded}
                    style={{ display: 'none' }}
                />
            )}
                                                               
        </div>
     </div>
    </main>
  )
}